/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { SelectChangeEvent } from '@mui/material';
import { LinearProgress, MenuItem } from '@mui/material';
import type { TFunction } from 'i18next';
import React from 'react';
import { TooltipSelect, dateUtils } from '@opengeoweb/shared';
import type { EDRInstance, Parameter } from '../../TimeSeries/types';

export const defaultInstanceId = 'latest-0';

export const ReferenceTimeSelect: React.FC<{
  rowIsEnabled: boolean;
  parameter: Parameter;
  refTimeList: EDRInstance[];
  t: TFunction;
  patchParameter: (parameter: Partial<Parameter>) => void;
}> = ({ rowIsEnabled, parameter, refTimeList, t, patchParameter }) => {
  const [referenceTime, setReferenceTime] = React.useState<string | undefined>(
    undefined,
  );
  const validRefTimes = React.useMemo(
    () =>
      refTimeList
        .filter((option) => {
          const date = dateUtils.parseCustomDateString(option.id);
          return dateUtils.isValid(date);
        })
        .sort((instance1, instance2) => {
          const time1 = dateUtils.parseCustomDateString(instance1.id);
          const time2 = dateUtils.parseCustomDateString(instance2.id);
          return time2.getTime() - time1.getTime();
        })
        .slice(0, 5),
    [refTimeList],
  );

  React.useEffect(() => {
    const initializeReferenceTime = (): void => {
      // Don't override if a "latest-" value is already selected
      if (referenceTime?.startsWith('latest-')) {
        return;
      }

      const referenceTimes = refTimeList.length > 0;

      if (referenceTimes) {
        const relativeReferenceTime =
          parameter.instanceId?.match(/^latest(?:-(\d+))?$/);
        if (relativeReferenceTime) {
          const index = Number(relativeReferenceTime[1] ?? 0);
          if (
            Number.isInteger(index) &&
            index >= 0 &&
            index < validRefTimes.length
          ) {
            setReferenceTime(
              parameter.instanceId === 'latest'
                ? defaultInstanceId
                : parameter.instanceId,
            );
            return;
          }
        }

        const alreadyHasReferenceTime =
          parameter.instanceId &&
          refTimeList.some((item) => item.id === parameter.instanceId);

        if (alreadyHasReferenceTime) {
          setReferenceTime(parameter.instanceId);
        } else {
          setReferenceTime(defaultInstanceId);
          if (parameter.instanceId !== defaultInstanceId) {
            // Update reference time for Echarts Plot
            patchParameter({
              id: parameter.id,
              instanceId: defaultInstanceId,
            });
          }
        }
      }
    };
    initializeReferenceTime();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refTimeList, parameter.instanceId, validRefTimes.length]);

  const isFetching = refTimeList.length === 0;

  const handleReferenceTimeChange = (
    event: SelectChangeEvent<unknown>,
  ): void => {
    const target = event.target as HTMLInputElement;

    if (target.value.startsWith('latest-')) {
      const relativeReferenceTimeRegexp = /^latest-(\d+)$/;
      const relativeSelection = relativeReferenceTimeRegexp.exec(target.value);
      if (relativeSelection) {
        const newParameter: Partial<Parameter> = {
          id: parameter.id,
          instanceId: target.value,
        };
        patchParameter(newParameter);
        setReferenceTime(target.value);
      }
    } else {
      const selectedRefTime = refTimeList.find((ref) => {
        return ref.id === target.value;
      });
      if (selectedRefTime) {
        const newParameter: Partial<Parameter> = {
          id: parameter.id,
          instanceId: target.value,
        };
        patchParameter(newParameter);
        setReferenceTime(target.value);
      } else {
        console.warn(`Unable to find reference time ${event.target.value}`);
      }
    }
  };

  if (!referenceTime) {
    return null;
  }

  return refTimeList &&
    refTimeList.length > 0 &&
    !refTimeList.some((value) => value.id === 'unvalidated') ? (
    <TooltipSelect
      SelectDisplayProps={{ 'aria-label': 'timeseriesManager-refTime' }}
      data-testid="parameterReferenceTimeSelect"
      isEnabled={rowIsEnabled}
      onChange={handleReferenceTimeChange}
      tooltip={t('timeseries-choose-ref-time')}
      value={referenceTime}
    >
      {!!isFetching && <LinearProgress color="inherit" data-testid="spinner" />}
      <MenuItem disabled>{t('timeseries-reference-time')}</MenuItem>
      {!!isFetching && (
        <MenuItem value={referenceTime}>
          {dateUtils.isValid(dateUtils.parseCustomDateString(referenceTime))
            ? dateUtils.dateToString(
                dateUtils.parseCustomDateString(referenceTime),
                "yyyy-MM-dd'T'HH:mm:ss 'Z'",
              )
            : referenceTime}
        </MenuItem>
      )}
      {validRefTimes.map((value, index) => (
        <MenuItem
          data-testid={`timeseries-ref-time-latest-${index}`}
          key={`latest-${value.id}`}
          value={`latest-${index}`}
        >
          {index > 0 ? `Latest -${index}` : 'Latest'}
        </MenuItem>
      ))}
      {refTimeList.map((value: EDRInstance) => (
        <MenuItem
          data-testid={`timeseries-ref-time-${value.id}`}
          key={value.id}
          value={value.id}
        >
          {dateUtils.isValid(dateUtils.parseCustomDateString(value.id))
            ? dateUtils.dateToString(
                dateUtils.parseCustomDateString(value.id),
                "yyyy-MM-dd'T'HH:mm:ss 'Z'",
              )
            : value.id}
        </MenuItem>
      ))}
    </TooltipSelect>
  ) : null;
};
